// お守り（ランダム生成の個体）の表示部品。台紙（レア度）＋お守りの絵（主効果）＋副効果。
// 表記：副効果の強さは Lv（●の数）。妖力の溜まりやすさには Lv を付けない。
import {
  OMAMORI,
  OMAMORI_SUB_LABEL,
  RARITY_LABEL,
  isFixedOmamoriSub,
  omamoriMainText,
  omamoriRangeText,
  omamoriSubText,
  type OmamoriItem,
  type OmamoriRoll,
  type OmamoriSub,
  type Rarity,
} from '@hyakki/core';
import { omamoriIconStyle, omikujiArt } from '../assets';
import { esc } from './dom';
import { icon } from './icons';

type OmLike = (OmamoriItem | OmamoriRoll) & { id?: number; locked?: boolean; paid?: boolean; legacy?: boolean };

export const rarityLabel = (r: Rarity) => RARITY_LABEL[r] ?? '並';
export const omName = (defId: string) => OMAMORI[defId]?.name ?? defId;

/** 台紙＋絵の小さな札（一覧・結果）。badges：装備中の枠番号・ロック */
export function omThumbHtml(o: OmLike, opts: { equippedAt?: number; size?: 's' | 'm' | 'l'; label?: boolean } = {}): string {
  const at = opts.equippedAt ?? -1;
  return `<span class="omt omt-${opts.size ?? 'm'} r-${o.rarity}">
    <span class="omt-plate" style="${omikujiArt.daishi(o.rarity)}"></span>
    <span class="omt-art om-art" style="${omamoriIconStyle(o.defId)}"></span>
    ${opts.label === false ? '' : `<em class="omt-rar">${rarityLabel(o.rarity)}</em>`}
    ${at >= 0 ? '<em class="omt-eq">装備</em>' : ''}
    ${o.locked ? `<em class="omt-lock" aria-label="ロック中">${icon('lock')}</em>` : ''}
    ${o.paid ? '<em class="omt-paid">購入</em>' : ''}
  </span>`;
}

/** 副効果の Lv の表示（●○）。妖力の溜まりやすさは Lv 無し */
export function subPips(s: OmamoriSub): string {
  if (isFixedOmamoriSub(s.id)) return `<span class="om-pips fixed" aria-label="妖力">${icon('sp')}</span>`;
  return `<span class="om-pips" aria-label="Lv${s.lv}">${'●'.repeat(s.lv)}<i>${'○'.repeat(Math.max(0, 5 - s.lv))}</i></span>`;
}

/** 詳細（名前・レア度・主効果・幅・品質・副効果） */
export function omDetailHtml(o: OmLike, opts: { compact?: boolean } = {}): string {
  const def = OMAMORI[o.defId];
  const subs = o.subs ?? [];
  const qPct = Math.round((o.q ?? 0) * 100);
  return `<div class="omd r-${o.rarity} ${opts.compact ? 'compact' : ''}">
    <header class="omd-head">${omThumbHtml(o, { size: opts.compact ? 'm' : 'l', label: false })}
      <div class="omd-name"><em class="omd-rar r-${o.rarity}">${rarityLabel(o.rarity)}</em><h3>${esc(def?.name ?? o.defId)}</h3>
      ${o.legacy ? '<small class="omd-legacy">旧お守りから引き継ぎ</small>' : ''}</div></header>
    <p class="omd-main"><b>主効果</b><span>${esc(def ? omamoriMainText(def, o.q) : '')}</span></p>
    <p class="omd-q"><span>効果の大きさ</span><span class="omd-bar"><i style="width:${qPct}%"></i></span><small>${esc(omamoriRangeText(o.defId))}</small></p>
    <ul class="omd-subs">${
      subs.length
        ? subs.map((s) => `<li>${subPips(s)}<span>${icon(SUB_ICON[s.id])}${esc(omamoriSubText(s))}</span></li>`).join('')
        : '<li class="muted">副効果なし</li>'
    }</ul>
  </div>`;
}

/** 副効果の印（一覧では文字の代わりに印＋Lv。詳細の行にも同じ印を付けて覚えられるように） */
const SUB_ICON: Record<OmamoriSub['id'], string> = { atk: 'attack', hp: 'heart', def: 'shield', agi: 'feather', skillPower: 'skill', buildHp: 'wall', buildPower: 'build', core: 'castle', neutral: 'oni', surge: 'sp' };
/** 副効果の印の並び（一覧の行用） */
export const subChipsHtml = (subs: readonly OmamoriSub[]) =>
  subs.length
    ? `<span class="om-subchips">${subs.map((s) => `<i class="om-subchip s-${s.id}" title="${esc(omamoriSubText(s))}">${icon(SUB_ICON[s.id])}${isFixedOmamoriSub(s.id) ? '' : `<b>${s.lv}</b>`}</i>`).join('')}</span>`
    : '<span class="om-subchips muted">—</span>';
/** 副効果の短い表示（確認の札など） */
export const subShort = (s: OmamoriSub) => (isFixedOmamoriSub(s.id) ? '妖力' : `${OMAMORI_SUB_LABEL[s.id]}${s.lv}`);
